import {
  CircularProgress,
  Link,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import unboundActions from "domain/core/actions";
import {
  getBillEstimate,
  getBillEstimateFetchStatus,
  getMonthlyElectricityUsage,
  getSelectedProduct,
  getSignupLitres,
  getUsageType,
} from "domain/core/selectors";
import { EvPlanRateCodes, UsageRates, UsageType } from "domain/core/types";
import { useActions, useReduxState } from "re-reduced";
import React, { ChangeEvent, useCallback, useState } from "react";
import { styled } from "styled-components";
import {
  ControlledCard,
  HeroTypography,
  RowLayout,
  RowLayoutLeft,
} from "ui/components/StyledComponents";
import { EvChargerAddOn } from "ui/compounds/EvChargerAddOn";
import { EvChargerDetailsDialog } from "ui/compounds/EvChargerDetailsDialog";
import { ExtraPlanPageText } from "ui/compounds/planDetails/ExtraPlanPageText";
import { PADDING } from "ui/theme";

const SCircularProgress = styled(CircularProgress)`
  margin: ${PADDING.medium};
`;

const CardContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SRowLayoutLeft = styled(RowLayoutLeft)`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: ${PADDING.medium};

  > * {
    margin-right: ${PADDING.medium};
  }
`;

const SSelect = styled(Select)`
  font-weight: 600;
`;

const TypographySm = styled(Typography)`
  padding: 0;
`;

const SLink = styled(Link)`
  margin-left: ${PADDING.xSmall};
`;

interface EvPlanProps {
  GSTInclusive: boolean;
  setGSTInclusive: (GSTInclusive: boolean) => void;
}

const stateSelectorMap = {
  billEstimate: getBillEstimate,
  billEstimateFetchStatus: getBillEstimateFetchStatus,
  campaignLitres: getSignupLitres,
  monthlyUsage: getMonthlyElectricityUsage,
  selectedProduct: getSelectedProduct,
  usageType: getUsageType,
};

export function EvPlan(props: EvPlanProps) {
  const reduxState = useReduxState(stateSelectorMap);
  const [evChargerDetailsModalOpen, setEvChargerDetailsModalOpen] =
    useState<boolean>(false);
  const actions = useActions(unboundActions);

  const handleGSTChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      // The input linked to this is a select which doesnt
      // accept booleans so we need to coerce a differnt type instead.
      const checked = Boolean(event.target.value);
      props.setGSTInclusive(checked);
    },
    [],
  );

  const handleEvChargerDetailsDialogOpen = useCallback(() => {
    setEvChargerDetailsModalOpen(true);
  }, []);

  const handleEvChargerDetailsDialogClose = useCallback(() => {
    setEvChargerDetailsModalOpen(false);
  }, []);

  const getAdjustedToCentsGSTPrice = useCallback(
    (value: string) => {
      if (props.GSTInclusive) {
        return (parseFloat(value) * 100 * 1.15).toFixed(2);
      }
      return (parseFloat(value) * 100).toFixed(2);
    },
    [props.GSTInclusive],
  );

  const handleUsageTypeChange = useCallback(
    (event: SelectChangeEvent<UsageType>) => {
      const usageType = event.target.value as UsageType;
      actions.setUsageType(usageType);
      actions.fetchBillingEstimate({
        usageType: usageType,
        locationClassification:
          reduxState.billEstimate?.location_classification,
      });
    },
    [],
  );

  const sortUsageRates = useCallback((usageRates: UsageRates[]) => {
    const baseRates = usageRates?.find((rateDetails) => {
      const rateCodes = rateDetails.rates.map((rate) => rate.rate_code);
      return rateCodes.every((rateCode) =>
        Object.values(EvPlanRateCodes).includes(rateCode as EvPlanRateCodes),
      );
    });
    const otherRates = usageRates.filter((rate) => rate !== baseRates);
    return {
      baseRates,
      otherRates,
    };
  }, []);

  const rateForRateCode = useCallback(
    (rateCode: EvPlanRateCodes, usageRates: UsageRates) => {
      if (!Boolean(usageRates?.rates)) return;
      const usageRate = usageRates.rates.find(
        (rate) => rate?.rate_code === rateCode,
      );
      return getAdjustedToCentsGSTPrice(usageRate.rate);
    },
    [props.GSTInclusive],
  );

  const renderResult = useCallback(() => {
    if (reduxState.billEstimateFetchStatus === "Pending") {
      return <SCircularProgress />;
    }

    // Successfully found address and bill estimate(s) (can be 1 or more)
    if (reduxState.billEstimateFetchStatus === "Fulfilled") {
      const supplyCharges = reduxState.billEstimate?.subscription_rates[0];
      const usageRates = reduxState.billEstimate?.usage_rates;
      const sortedRates = sortUsageRates(usageRates);

      return (
        <div>
          {Boolean(sortedRates?.otherRates?.length) && (
            <>
              <Typography component="p">
                At your address, you have an additional meter(s) which is priced
                separately:
              </Typography>
              <ul>
                {sortedRates?.otherRates.map((otherRateDetails) => {
                  return otherRateDetails.rates.map((rateDetail) => {
                    return (
                      <li>
                        {otherRateDetails.channels[0].content_name} meter will
                        be charged at{" "}
                        {getAdjustedToCentsGSTPrice(rateDetail.rate)} c/kWh{" "}
                        {props.GSTInclusive ? "incl." : "excl."} GST.
                      </li>
                    );
                  });
                })}
              </ul>
              <Typography component="p">
                The prices above only apply to the meters listed. Any other
                meter usage will be priced as outlined in the table below.
              </Typography>
            </>
          )}
          <ExtraPlanPageText />
          <SRowLayoutLeft>
            <Typography variant="subtitle1">Pricing based on:</Typography>
            <SSelect
              autoWidth
              defaultValue={reduxState.usageType}
              onChange={handleUsageTypeChange}
            >
              <MenuItem value={UsageType.STANDARD}>
                {UsageType.STANDARD} power user
              </MenuItem>
              <MenuItem value={UsageType.LOW}>
                {UsageType.LOW} power user
              </MenuItem>
            </SSelect>
            <SSelect
              autoWidth
              defaultValue={Number(props.GSTInclusive)}
              onChange={handleGSTChange}
            >
              <MenuItem value={1}>incl. gst</MenuItem>
              <MenuItem value={0}>excl. gst</MenuItem>
            </SSelect>
            *For more information see our{" "}
            <SLink
              href={`${process.env.REACT_APP_Z_WEBSITE_URL}power/tc-ev`}
              target="_blank"
            >
              Terms and Conditions
            </SLink>
          </SRowLayoutLeft>
          <CardContainer>
            <ControlledCard>
              <HeroTypography variant="h3">
                3 free charging hours
              </HeroTypography>
              <Typography variant="body2" component="p">
                Between the hours of 3am and 6am everyday.*
              </Typography>
              <Typography variant="caption" component="p">
                Make the most of your 3 free hours by adding an Evnex smart
                charger to schedule your charging sessions while you sleep.
              </Typography>
            </ControlledCard>
            <ControlledCard>
              <HeroTypography variant="h3">Great prices</HeroTypography>
              <Typography variant="body2" component="p">
                Your personalised pricing:*
              </Typography>
              <RowLayout>
                <TypographySm variant="caption">Daily Charge: </TypographySm>
                <TypographySm variant="caption">
                  {getAdjustedToCentsGSTPrice(supplyCharges.rates[0].rate)}c/day
                </TypographySm>
              </RowLayout>
              <RowLayout>
                <TypographySm variant="caption" key={"dayRate"}>
                  Day rate (7am-9pm):
                </TypographySm>
                <TypographySm variant="caption">
                  {rateForRateCode(EvPlanRateCodes.day, sortedRates?.baseRates)}
                  c/kWh
                </TypographySm>
              </RowLayout>
              <RowLayout>
                <TypographySm variant="caption" key={"nightRate"}>
                  Night rate (9pm-7am):
                </TypographySm>
                <TypographySm variant="caption">
                  {rateForRateCode(
                    EvPlanRateCodes.night,
                    sortedRates?.baseRates,
                  )}
                  c/kWh
                </TypographySm>
              </RowLayout>
              <RowLayout>
                <TypographySm variant="caption" key={"freeRate"}>
                  Free rate (3am-6am):
                </TypographySm>
                <TypographySm variant="caption">
                  {rateForRateCode(
                    EvPlanRateCodes.free,
                    sortedRates?.baseRates,
                  )}
                  c/kWh
                </TypographySm>
              </RowLayout>
            </ControlledCard>
            <ControlledCard>
              <HeroTypography variant="h3">
                Half price rate at nights
              </HeroTypography>
              <Typography variant="body2">
                From 9pm to 7am everyday!*
              </Typography>
              <Typography variant="caption">
                Take advantage of cheaper electricity while you sleep.
              </Typography>
            </ControlledCard>
          </CardContainer>
        </div>
      );
    }
  }, [
    props.GSTInclusive,
    handleGSTChange,
    reduxState.billEstimate,
    reduxState.billEstimateFetchStatus,
  ]);

  return (
    <>
      {renderResult()}
      <EvChargerAddOn
        onEvChargerDetailsDialogOpen={() => {
          handleEvChargerDetailsDialogOpen();
        }}
      />
      <EvChargerDetailsDialog
        visible={evChargerDetailsModalOpen}
        onClose={handleEvChargerDetailsDialogClose}
        GSTInclusive={props.GSTInclusive}
      />
    </>
  );
}
